/**
 * useConfirm Hook
 *
 * Provides a confirmation dialog for destructive actions.
 * Returns a confirm function that shows a modal and returns a promise.
 */

import { useState, useCallback } from 'react';
import { Modal } from '../components/ui/Modal';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'danger' | 'warning' | 'primary';
}

export interface ConfirmDialogState extends ConfirmOptions {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Hook for confirmation dialogs
 */
export const useConfirm = () => {
  const [dialogState, setDialogState] = useState<ConfirmDialogState | null>(null);

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setDialogState({
        ...options,
        isOpen: true,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        confirmVariant: options.confirmVariant || 'primary',
        onConfirm: () => {
          setDialogState(null);
          resolve(true);
        },
        onCancel: () => {
          setDialogState(null);
          resolve(false);
        },
      });
    });
  }, []);

  const ConfirmDialog = dialogState ? (
    <Modal
      isOpen={dialogState.isOpen}
      onClose={dialogState.onCancel}
      size="md"
    >
      <Modal.Header>{dialogState.title}</Modal.Header>
      <Modal.Body>
        <p className="text-sm text-gray-600">{dialogState.message}</p>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={dialogState.onCancel}
        >
          {dialogState.cancelText}
        </Button>
        <Button
          variant={dialogState.confirmVariant}
          onClick={dialogState.onConfirm}
        >
          {dialogState.confirmText}
        </Button>
      </Modal.Footer>
    </Modal>
  ) : null;

  return {
    confirm,
    ConfirmDialog,
  };
};
